import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { WizardNavigationComponent } from '../../../../shared/components/wizard-navigation/wizard-navigation.component';
import { SharedService } from '../../../../shared/services/shared.service';
import { CredentialDocument } from '../dental-credentials/dental-credentials.component';
import { DocumentCategory, DocumentSubCategory, DocumentSubCategoryName, Specialities } from '../../../../shared/constants/document.constants';
import { CheckboxComponent, ButtonComponent } from "ddmo-storybook-lib";
import { FormBuilder, Validators } from '@angular/forms';
import { v4 as uuidv4 } from 'uuid';
import { DentalCredentialsService } from '../../services/dental-credentials.service';
import { MatDialog } from '@angular/material/dialog';
import { PopupComponent } from '../popup/popup.component';
import { LoaderService } from '../../../../shared/services/loader.service';
import { PDFDocument } from 'pdf-lib';
import { UploadTriggerComponent } from '../../../../shared/components/upload-trigger/upload-trigger.component';

interface Document {
    id: number;
    title: string;
    subtitle: string;
    isSigned?: boolean;
    srcUrl?: string;
    hide?: boolean;
}

interface PopupMessageSection {
    title: string;
    items: string[];
}

interface PopupStructuredMessage {
    intro: string;
    sections: PopupMessageSection[];
    footer?: string;
}

export enum DocumentTypes {
    W9 = 1,
    ParticipatingAgreement = 2,
    UniformRequirements = 3,
}

@Component({
    selector: 'app-signed-documents',
    imports: [CommonModule, WizardNavigationComponent, CheckboxComponent, ButtonComponent, UploadTriggerComponent],
    templateUrl: './signed-documents.component.html',
    styleUrl: './signed-documents.component.scss'
})
export class SignedDocumentsComponent implements OnInit {
    DocumentTypes = DocumentTypes;
    documents: Document[] = [
        {
            id: DocumentTypes.W9,
            title: 'W9',
            subtitle: 'Required for onboarding',
            srcUrl: '/assets/icons/Icon-W9.jpg',
            hide: true,
        },
        {
            id: DocumentTypes.ParticipatingAgreement,
            title: 'Participating agreement',
            subtitle: 'Required for onboarding',
            srcUrl: '/assets/icons/Icon-Agreement.jpg',
            hide: true,
        },
        {
            id: DocumentTypes.UniformRequirements,
            title: 'Uniform requirements',
            subtitle: 'Required for onboarding',
            srcUrl: '/assets/icons/Icon-Uniform.jpg',
            hide: false
        }
    ];

    hasSignedDocument = false;

    documentsMap: Record<string, CredentialDocument[]> = {
        W9: [],
        participatingAgreement: [],
        uniformRequirements: [],
    };
    checkboxLabel = 'I have read and reviewed the Uniform Requirements.';
    termscheckForm = this.fb.group({
        Attestation: [{ value: false, disabled: true }, Validators.requiredTrue]
    });
    confirmationCheckbox: any;
    credentialPayload: any;
    isUniformRequirementsRead = false;
    showNotReviewed = false;
    @ViewChild('w9Uploader') w9Uploader!: UploadTriggerComponent;
    @Input() accept = 'application/pdf';
    action: any = '';
    impersonatedUser: any = localStorage.getItem("impersonatedUser") ?? ""
    ishasParLocation = false;
    constructor(
        private router: Router, private sharedService: SharedService,
        private route: ActivatedRoute, private fb: FormBuilder,
        private dentalCredentialingService: DentalCredentialsService,
        private dialog: MatDialog, private loaderService: LoaderService,
    ) { }

    ngOnInit() {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        this.route.queryParams.subscribe(params => {
            if (params['signed'] === 'true') {
                this.hasSignedDocument = true;
            }
            this.credentialPayload = JSON.parse(localStorage.getItem('getCredentialpayload')!);
            if (localStorage.getItem('documents')?.length) {
                const documents = JSON.parse(localStorage.getItem('documents')!);
                this.patchValues(documents);
            }
            this.documents[1].hide = false;
            this.enableFullNameAfterCheckbox();
        });
    }

    validateLocation(location: any): any {
        if (!this.ishasParLocation) {
            let isExistingPar = this.setParticipating(location);
            if (isExistingPar) {
                this.ishasParLocation = true;
                const loggedUserDetails = JSON.parse(localStorage.getItem("loggedUserDetails") || '{}')
                let isMO = loggedUserDetails?.licenseState == "MO" ? true : false;
                this.documents[1].hide = (isMO && this.ishasParLocation) ? false : true;
            }
        }
    }

    setParticipating(selectedLoc: any) {
        let network = selectedLoc.providerNetwork;
        let isLocationFromTinSearch = selectedLoc.isLocationFromTINSearch ? selectedLoc.isLocationFromTINSearch : ""
        const whLocations: any[] = JSON.parse((localStorage.getItem('whLocations') || '[]'))
        if (!network && selectedLoc.esLocationId) {
            const match = whLocations?.find(
                (wh: any) => wh.esLocationId === selectedLoc.esLocationId
            );
            network = match?.providerNetwork;
        }
        if (isLocationFromTinSearch || !selectedLoc?.esLocationId) {
            if (selectedLoc.isParticipating == true) {  // this one is for TIN search
                return true
            } else { return false }
        }
        if (network == 'Participating') { return true; }
        if (network == 'Not participating') { return false; }
        return null;
    }

    onReadAndSign(document: Document) {
        if (document.id == DocumentTypes.UniformRequirements) {
            this.isUniformRequirementsRead = true;
            localStorage.setItem('isUniformRequirementsRead', 'true');
        }
        if (document.id == DocumentTypes.ParticipatingAgreement) {
            const missingFields = this.getParticipatingAgreementMissingFields();
            if (missingFields.length > 0) {
                this.dialog.open(PopupComponent, {
                    disableClose: true,
                    data: {
                        headerMessage: 'Please complete the required information in the following sections before signing the Participating Agreement:',
                        message: this.buildParticipatingAgreementPopupMessage(missingFields),
                        isPTESaved: true
                    },
                    width: '550px',
                    backdropClass: '',
                    hasBackdrop: true,
                });
                return;
            }
        }
        this.router.navigate(['/credentialing/sign-documents/sign'], {
            queryParams: { title: document.title }
        });
    }

    onView(document: Document) {
    }

    onBack() {
        this.router.navigate(['/credentialing/locations']);
    }

    onNext() {
        this.router.navigate(['/credentialing/confirmation']);
    }

    enableReviewed() {
        this.showNotReviewed = true;
    }

    async getCredentialData() {
        const loginUserId = localStorage.getItem("loggedUserId");
        this.credentialPayload = JSON.parse(localStorage.getItem('getCredentialpayload')!);
        let getCredentialpayload = {
            "npi": this.credentialPayload['npi'],
            "licenseNumber": this.credentialPayload['licenseNumber'],
            "userId": loginUserId || -1
        }
        this.sharedService.getCredentiallingData(getCredentialpayload).subscribe({
            next: (results: any) => {
                localStorage.setItem('credentialingEvent', JSON.stringify(results['credentialingEvent']));
                localStorage.setItem('documents', JSON.stringify(results['documents']));
                localStorage.setItem('credentialingEventId', results['credentialingEvent']['credentialingEventId']);

                this.patchValues(results['documents']);
            },
            error: (err) => {
                this.sharedService.logError("APIFailed", "FileSigned-documentsComponent - getCredentiallingData, Method:getCredentialData", this.router.url.split('?')[0], err).subscribe();
            }
        });
    }

    patchValues(documents: any) {
        this.documentsMap = {
            W9: [],
            participatingAgreement: [],
            uniformRequirements: [],
        };
        for (const doc of documents) {
            if (doc.categoryId == DocumentCategory.SignedDocument) {
                switch (doc.subCategoryId) {
                    case DocumentSubCategory.W9Form:
                        this.documents[0].isSigned = doc.isSigned;
                        this.documentsMap['W9'].push({
                            originalFileName: `${doc.fileName}.pdf`,
                            name: `W9Form.pdf`,
                            isSigned: doc.isSigned
                        });
                        break;

                    case DocumentSubCategory.ParticipatingAgreement:
                        this.documents[1].isSigned = doc.isSigned;
                        this.documentsMap['participatingAgreement'].push({
                            originalFileName: `${doc.fileName}.pdf`,
                            name: `participatingAgreement.pdf`,
                            isSigned: doc.isSigned
                        });
                        break;

                    case DocumentSubCategory.UniformRequirements:
                        this.documents[2].isSigned = doc.isSigned;
                        this.termscheckForm.get('Attestation')?.setValue(true);
                        this.documentsMap['uniformRequirements'].push({
                            originalFileName: `${doc.fileName}.pdf`,
                            name: `uniformRequirements.pdf`,
                            isSigned: doc.isSigned
                        });
                        break;
                }
            }
        }
        localStorage.setItem("signedDocuments", JSON.stringify(this.documentsMap));
    }

    enableFullNameAfterCheckbox() {
        this.termscheckForm.get('Attestation')?.valueChanges.subscribe(
            checked => {
                if (checked) {
                    this.confirmationCheckbox = false
                } else {
                    if (this.termscheckForm.get('Attestation')?.touched) {
                        this.confirmationCheckbox = true
                    }
                }
            }
        );
    }

    async termsCheckboxCheck() {
        this.loaderService.show();
        this.termscheckForm.get('Attestation')?.setValue(this.termscheckForm.get('Attestation')?.value ? false : true)
        this.termscheckForm.get('Attestation')?.updateValueAndValidity();
        const isCheckBoxTrue = this.termscheckForm.get('Attestation')?.value;
        this.confirmationCheckbox = !isCheckBoxTrue;
        if (isCheckBoxTrue) await this.saveDocument();
        else await this.onDeleteDocument(null, 2);
        this.loaderService.hide();
    }

    async saveDocument() {
        const sub_category = DocumentSubCategory.UniformRequirements;
        this.credentialPayload = JSON.parse(localStorage.getItem('getCredentialpayload')!);

        let date = new Date();

        let year = date.getFullYear();
        let month = String(date.getMonth() + 1).padStart(2, '0');
        let day = String(date.getDate()).padStart(2, '0');
        const uniqueId = uuidv4();
        const filename = `${year}${month}${day}_${this.credentialPayload['npi']}_${uniqueId}`;

        let file = await this.generateFile(filename);
        const response = await this.uploadDocument(file, filename, sub_category);
        const documents = JSON.parse(localStorage.getItem('documents') || '[]');

        const payload = {
            documentId: response.documentId,
            category: 'SignedDocument',
            categoryId: DocumentCategory.SignedDocument,
            credentialingEventId: localStorage.getItem('credentialingEventId'),
            fileName: filename,
            isSigned: true,
            subCategoryId: sub_category,
            subcategory: DocumentSubCategoryName[sub_category]
        };

        const index = documents.findIndex(
            (item: any) => item.subCategoryId === sub_category
        );

        if (index !== -1) {
            documents.splice(index, 1);
        }
        this.documentsMap['uniformRequirements'] = [];
        this.documentsMap['uniformRequirements'].push({
            originalFileName: `${filename}.pdf`,
            name: `uniformRequirements.pdf`,
            isSigned: true
        });
        documents.push(payload);
        localStorage.setItem('documents', JSON.stringify(documents));
        return true;
    }

    async onDeleteDocument(doc: any = null, index: any = null) {
        this.loaderService.show();
        const loginUserId = localStorage.getItem("loggedUserId");
        const credentialingEventId = localStorage.getItem('credentialingEventId');
        const filename = doc ? doc.originalFileName.split(".")[0] : (this.documentsMap['uniformRequirements'] && this.documentsMap['uniformRequirements'][0]
            && this.documentsMap['uniformRequirements'][0]['originalFileName']
        ) ? this.documentsMap['uniformRequirements'][0]['originalFileName'].split(".")[0] : null;
        const requestParams = {
            "credentialingEventID": Number(credentialingEventId),
            "fileName": filename,
            "modifiedUserID": loginUserId ? Number(loginUserId) : -1
        }
        this.dentalCredentialingService.deleteDocument(requestParams).subscribe({
            next: async (response: any) => {
                this.documentsMap['uniformRequirements'] = [];
                const documents = JSON.parse(localStorage.getItem('documents') || '[]');
                const sub_category = doc ? DocumentSubCategory.W9Form : DocumentSubCategory.UniformRequirements;
                const _index = documents.findIndex(
                    (item: any) => item.subCategoryId === sub_category
                );

                if (_index !== -1) {
                    documents.splice(_index, 1);
                }
                localStorage.setItem('documents', JSON.stringify(documents));
                this.documents[index].isSigned = false;
                this.loaderService.hide();
            }, error: (err: any) => {
                try {
                    this.sharedService.logError("APIFailed", "FileSigned-documentsComponent - deleteDocument, Method:onDeleteDocument", this.router.url.split('?')[0], err).subscribe();
                } catch (error: any) {
                }
                this.loaderService.hide();
            }
        })
    }

    async generateFile(fileName: any) {
        return new File([], fileName, {
            type: 'application/pdf',
            lastModified: Date.now()
        });
    }

    onCancel() {
        this.router.navigate(['/landing']);
    }

    onViewDocument(doc: any, isMasterFile: boolean) {
        this.loaderService.show();
        let filename = isMasterFile ? 'w9_form.pdf' : doc.originalFileName;
        this.dentalCredentialingService
            .viewAzureDocument(isMasterFile ? filename : filename?.split(".")[0])
            .subscribe({
                next: async (response: any) => {
                    let blob: Blob = response.body;
                    // 🔒 Modify PDF only for master file
                    if (!isMasterFile) {
                        const pdfBytes = await blob.arrayBuffer();
                        const pdfDoc = await PDFDocument.load(pdfBytes, {
                            ignoreEncryption: true
                        });
                        try {
                            const form = pdfDoc.getForm();
                            const fields = form.getFields();
                            fields.forEach(field => {
                                field.enableReadOnly();   // make fields readonly
                            });
                            form.flatten(); // convert to static
                        } catch (e) {
                        }
                        const updatedPdf: any = await pdfDoc.save();
                        blob = new Blob([updatedPdf], { type: 'application/pdf' });
                    }
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = isMasterFile ? 'w9_form.pdf' : `Signed_${doc.name}`;
                    link.click();
                    window.URL.revokeObjectURL(url);
                    this.loaderService.hide();
                },
                error: (err: any) => {
                    this.sharedService.logError(
                        "APIFailed",
                        "FileDental-SignedDocuments - viewAzureDocument, Method:onViewDocument",
                        this.router.url.split('?')[0],
                        err
                    ).subscribe();
                    this.loaderService.hide();
                }
            });
    }

    triggerFileSelect() {
        this.w9Uploader?.openPicker();
    }

    async onUploadDocument(file: File, index: any) {
        this.loaderService.show();
        if (!file) return;
        const type = 'W9';
        this.credentialPayload = JSON.parse(localStorage.getItem('getCredentialpayload')!);
        this.documentsMap[type] ??= [];

        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        const uniqueId = uuidv4();
        const ext = file.name.split('.').pop();
        if (!ext?.includes('pdf')) {
            const alertMessage = 'Only PDF is accepted.';
            const dialogRef = this.dialog.open(PopupComponent, {
                disableClose: true,
                data: {
                    headerMessage: 'Alert',
                    message: alertMessage, isPTESaved: true
                },
                width: '550px',
                backdropClass: '',
                hasBackdrop: true,
            });
            this.loaderService.hide();
            return;
        }
        const filename = `${year}${month}${day}_${this.credentialPayload['npi']}_${uniqueId}`;

        try {
            const subCategoryId = DocumentSubCategory.W9Form;
            const response: any = await this.uploadDocument(file, filename, subCategoryId);
            this.documents[0].isSigned = true;

            const documents = JSON.parse(localStorage.getItem('documents') || '[]');

            const payload = {
                documentId: response.documentId,
                category: 'SignedDocument',
                categoryId: DocumentCategory.SignedDocument,
                credentialingEventId: localStorage.getItem('credentialingEventId'),
                fileName: filename,
                isSigned: true,
                subCategoryId,
                subcategory: DocumentSubCategoryName[subCategoryId]
            };

            const removingIndex = documents.findIndex(
                (item: any) => item.subCategoryId === subCategoryId
            );

            if (removingIndex !== -1) {
                documents.splice(removingIndex, 1);
            }
            this.documentsMap['W9'] = [];
            this.documentsMap['W9'].push({
                originalFileName: `${filename}.pdf`,
                name: `W9Form.pdf`,
                isSigned: true
            });

            documents.push(payload);
            localStorage.setItem('documents', JSON.stringify(documents));
            this.loaderService.hide();
        } catch (error) {
            try {
                this.sharedService.logError("APIFailed", "FileDental-SignedDocuments - saveDocument, Method:onUploadDocument", this.router.url.split('?')[0], error).subscribe();
            } catch (error) {
            }
            this.loaderService.hide();
        }
    }

    async uploadDocument(file: any, filename: any, subCategoryId: any) {
        const loginUserId = localStorage.getItem("loggedUserId");

        const params = {
            "credentialingEventId": localStorage.getItem('credentialingEventId'),
            "categoryId": DocumentCategory.SignedDocument,
            "subCategoryId": subCategoryId,
            "fileName": filename,
            "isSigned": true,
            "userId": loginUserId || -1
        }
        return await this.dentalCredentialingService.saveDocument(file, params);
    }

    getAction(): any {
        const el = JSON.parse(localStorage.getItem('dentistInfo') || '{}') || {};
        const credentialingEvent = JSON.parse(localStorage.getItem('credentialingEvent') || '{}');
        const eventStatus = credentialingEvent?.eventStatus;

        if (!el?.reCredentialDate) {
            return false;
        }
        const recredDate: Date = this.parseYyyyMmDd("" + el.reCredentialDate);

        if (!recredDate) {
            return false;
        }
        const locations = JSON.parse(localStorage.getItem('whLocations') || '[]');
        const isNonPar =
            locations.length > 0 &&
            locations.every(
                (loc: any) =>
                    loc?.providerNetwork?.toLowerCase() === 'not participating'
            );
        if (isNonPar) {
            return false;
        }
        const today = new Date();
        const dueDate = new Date(recredDate);
        const value = Number(this.sharedService.credentialing_graceperiod);
        const gracePeriod = !value || isNaN(value) ? 180 : value;

        dueDate.setDate(dueDate.getDate() - gracePeriod);

        //  Block only if TOO EARLY (more than 180 days before)
        if (today < dueDate) {
            return false;
        }

        return true;
    }

    parseYyyyMmDd(dateString: string): Date {
        const year = Number(dateString.substring(0, 4));
        const month = Number(dateString.substring(4, 6)) - 1;
        const day = Number(dateString.substring(6, 8));

        return new Date(year, month, day);
    }

    get isParticipatingAgreementEnabled(): boolean {
        return this.getParticipatingAgreementMissingFields().length === 0;
    }

    private getParticipatingAgreementMissingFields(): string[] {
        const missingFields: string[] = [];
        const demographics = JSON.parse(localStorage.getItem('demographics') || '{}');
        const credentials = JSON.parse(localStorage.getItem('credential') || '[]');
        const locations = JSON.parse(localStorage.getItem('locations') || '[]');
        const loggedUserDetails = JSON.parse(localStorage.getItem('loggedUserDetails') || '{}');
        const credentialPayload = JSON.parse(localStorage.getItem('getCredentialpayload') || '{}');

        const primaryLocation = Array.isArray(locations)
            ? locations.find((loc: any) => loc?.isPrimary === true)
            : null;

        const firstName = demographics?.firstName || loggedUserDetails?.firstName || '';
        const lastName = demographics?.lastName || loggedUserDetails?.lastName || '';
        const dentistName = `${firstName} ${lastName}`.trim();

        if (!dentistName) {
            missingFields.push('Dentist Name');
        }
        if (!demographics?.DOB) {
            missingFields.push('Date Of Birth');
        }
        const hasStreetAddress = this.hasAnyNonEmptyValue(primaryLocation, ['address', 'address1', 'address2']);
        if (!hasStreetAddress) {
            missingFields.push('Street Address');
        }
        if (!primaryLocation?.city) {
            missingFields.push('City');
        }
        if (!primaryLocation?.state) {
            missingFields.push('State');
        }
        if (!primaryLocation?.zip) {
            missingFields.push('Zipcode');
        }
        if (!primaryLocation?.voicePhone) {
            missingFields.push('Phone');
        }
        if (!primaryLocation?.tin) {
            missingFields.push('SSN or Tax Id');
        }
        if (!credentials?.[0]?.stateLicenseNumber) {
            missingFields.push('MO state license');
        }
        if (!credentials?.[0]?.stateLicenseEffectiveDate) {
            missingFields.push('State License Effective Date');
        }
        if (!credentialPayload?.npi) {
            missingFields.push('NPI Type 1');
        }
        if (!primaryLocation) {
            missingFields.push('Primary Location');
        }

        const requiresSpecialtyLicense =
            loggedUserDetails?.licenseState === 'SC' &&
            demographics?.specialty &&
            demographics?.specialty?.toString() !== Specialities.GeneralDentistry.toString();

        if (requiresSpecialtyLicense && !credentials?.[0]?.specialtyLicenseNumber) {
            missingFields.push('Speciality license');
        }

        return missingFields;
    }

    private buildParticipatingAgreementPopupMessage(missingFields: string[]): PopupStructuredMessage {
        const fieldMappings: Record<string, { section: string; label: string }> = {
            'Dentist Name': { section: 'Dentist Information', label: 'Dentist Name' },
            'Date Of Birth': { section: 'Dentist Information', label: 'Date of Birth' },
            'MO state license': { section: 'Dental Credentials', label: 'State License' },
            'State License Effective Date': { section: 'Dental Credentials', label: 'Effective Date' },
            'Speciality license': { section: 'Dental Credentials', label: 'Specialty License' },
            'NPI Type 1': { section: 'Locations', label: 'Mark at least one location as **Primary**' }
        };

        const locationFields = new Set([
            'Primary Location',
            'Street Address',
            'City',
            'State',
            'Zipcode',
            'Phone',
            'SSN or Tax Id',
            'NPI Type 1'
        ]);

        const sectionOrder = ['Dentist Information', 'Dental Credentials', 'Locations'];
        const groupedSections = new Map<string, string[]>();
        const hasLocationGaps = missingFields.some((field: string) => locationFields.has(field));

        if (hasLocationGaps) {
            groupedSections.set('Locations', ['Mark at least one location as **Primary**']);
        }

        for (const field of missingFields) {
            if (locationFields.has(field)) {
                continue;
            }

            const mapping = fieldMappings[field] || { section: 'Dentist Information', label: field };
            const existing = groupedSections.get(mapping.section) || [];
            if (!existing.includes(mapping.label)) {
                existing.push(mapping.label);
            }
            groupedSections.set(mapping.section, existing);
        }

        const sections: PopupMessageSection[] = sectionOrder
            .map((sectionTitle: string) => ({
                title: sectionTitle,
                items: groupedSections.get(sectionTitle) || []
            }))
            .filter((section: PopupMessageSection) => section.items.length > 0);

        return {
            intro: '',
            sections,
            footer: 'Once all required information is completed, return to the Participating Agreement page to sign.'
        };
    }

    isParticipatingAgreement(doc: any): boolean {
        return doc.id === DocumentTypes.ParticipatingAgreement;
    }

    isW9(doc: any): boolean {
        return doc.title === 'W9';
    }

    canShowParticipatingAgreement(doc: any): boolean {
        return !doc.hide;
    }

    canShowReadAndSign(doc: any): boolean {
        return doc.id !== DocumentTypes.UniformRequirements && !this.isW9(doc);
    }

    canShowView(doc: any): boolean {
        return doc.isSigned || doc.id === DocumentTypes.UniformRequirements;
    }

    private hasAnyNonEmptyValue(source: any, keys: string[]): boolean {
        if (!source || !Array.isArray(keys) || keys.length === 0) {
            return false;
        }

        return keys.some((key: string) => {
            const value = source?.[key];
            if (value === null || value === undefined) {
                return false;
            }

            return String(value).trim().length > 0;
        });
    }

}
